import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { getTenant } from '../../api/tenants';
import { listBoundaries } from '../../api/boundaries';
import { listLinkedParcelRefs, getParcelCacheStatus } from '../../api/parcels';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Star as StarIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

import { MapCanvas } from '@/components/maps/MapCanvas';
import { MapLegend } from '@/components/maps/MapLegend';
import { ParcelDetailDrawer } from '@/components/maps/ParcelDetailDrawer';
import MemberLinkDialog from '../../components/fpa/MemberLinkDialog';
import { getMemberCached } from '../../api/fpaMembers';
import {
  PARCEL_LEGEND_ITEMS,
  getParcelRef,
  formatParcelLabel,
} from '@/utils/parcelGeometry';
import { colorForBoundaryIndex } from '@/utils/boundaryGeometry';

/**
 * Admin parcels map. Rendering, layering, labels, caching, and linking are all
 * handled by the shared `MapCanvas` + `ParcelOverlay`/`BoundariesOverlay`; this
 * page only supplies page-specific enrichment (member hazards/contacts feeding
 * the labels + detail drawer) and chrome (summary panel, legend, link dialog).
 */
const TenantParcelsMap = () => {
  const { id } = useParams();
  const tenantId = Number(id);

  const [tenant, setTenant] = useState(null);
  const [boundaries, setBoundaries] = useState([]);
  const [pageError, setPageError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [parcelCount, setParcelCount] = useState(null);

  // Linked-ref set + parcel_ref → member_ids, for counts and the member
  // enrichment pipeline below.
  const [linkedRefs, setLinkedRefs] = useState(() => new Set());
  const [refToMemberIds, setRefToMemberIds] = useState(() => new Map());
  // parcel_ref → hazard tags (drives hazard-coloured labels in the overlay).
  const [refToHazards, setRefToHazards] = useState(() => new Map());
  // parcel_ref → [{ id, name, ref_number, contacts }] for the detail drawer.
  const [refToMembers, setRefToMembers] = useState(() => new Map());

  // Bumped after a link/unlink so both the page link set and the overlay's
  // colouring refetch.
  const [refreshKey, setRefreshKey] = useState(0);
  // Selected parcel for the bottom detail drawer; link dialog target.
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);

  // ── Tenant + boundaries ──────────────────────────────────────────────── //
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tenantData, boundariesData] = await Promise.all([
          getTenant(tenantId),
          listBoundaries({ tenant_id: tenantId, page_size: 200 }),
        ]);
        if (cancelled) return;
        setTenant(tenantData);
        setBoundaries(boundariesData?.boundaries || []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load tenant + boundaries:', err);
        setPageError(err.response?.data?.detail || err.message || 'Failed to load');
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getParcelCacheStatus();
        if (!cancelled) setCacheStatus(s);
      } catch (err) {
        if (!cancelled) console.warn('cache-status probe failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Link set + member_ids (for counts + enrichment). Refetched on refreshKey.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listLinkedParcelRefs(tenantId);
        if (cancelled) return;
        setLinkedRefs(new Set(data?.linked_refs || []));
        const map = new Map();
        for (const link of data?.links || []) {
          const arr = map.get(link.parcel_ref) || [];
          arr.push(link.member_id);
          map.set(link.parcel_ref, arr);
        }
        setRefToMemberIds(map);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to load linked refs:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, refreshKey]);

  // ── Resolve linked members (cache-first) → hazards + contacts per parcel.
  useEffect(() => {
    if (refToMemberIds.size === 0) {
      setRefToHazards(new Map());
      setRefToMembers(new Map());
      return undefined;
    }
    let cancelled = false;
    const uniqueIds = Array.from(new Set(Array.from(refToMemberIds.values()).flat()));
    (async () => {
      const results = await Promise.allSettled(uniqueIds.map((mid) => getMemberCached(mid)));
      if (cancelled) return;
      const memberById = new Map();
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) memberById.set(uniqueIds[i], r.value);
      });

      const hazardsByRef = new Map();
      const membersByRef = new Map();
      for (const [ref, ids] of refToMemberIds.entries()) {
        const hazardSet = new Set();
        const summaries = [];
        for (const mid of ids) {
          const m = memberById.get(mid);
          for (const t of m?.hazard_tags || m?.hazards || []) hazardSet.add(t);
          summaries.push({
            id: mid,
            name: m?.name ?? null,
            ref_number: m?.ref_number ?? null,
            contacts: m?.contacts ?? [],
          });
        }
        if (hazardSet.size > 0) hazardsByRef.set(ref, Array.from(hazardSet));
        membersByRef.set(ref, summaries);
      }
      setRefToHazards(hazardsByRef);
      setRefToMembers(membersByRef);
    })();
    return () => { cancelled = true; };
  }, [refToMemberIds]);

  const handleParcelSelect = useCallback((parcel) => {
    setSelectedParcel(parcel);
  }, []);

  const handleManageMembers = useCallback(() => {
    if (!selectedParcel) return;
    setLinkTarget({
      parcelRef: getParcelRef(selectedParcel) || '',
      parcelLabel: formatParcelLabel(selectedParcel) || getParcelRef(selectedParcel) || '',
    });
    setSelectedParcel(null);
  }, [selectedParcel]);

  const handleLinkChanged = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const legendItems = useMemo(() => {
    const items = [...PARCEL_LEGEND_ITEMS];
    boundaries.forEach((b, i) => {
      const c = colorForBoundaryIndex(i);
      items.push({
        section: 'Boundaries',
        label: b.name || b.boundary_name || `Boundary ${b.id}`,
        color: c,
        fillColor: c,
        dashed: true,
      });
    });
    return items;
  }, [boundaries]);

  const center = tenant?.latitude && tenant?.longitude
    ? [Number(tenant.latitude), Number(tenant.longitude)]
    : [-25.6429, 28.3433];

  const selectedRef = selectedParcel ? getParcelRef(selectedParcel) : null;
  const selectedMembers = (selectedRef && refToMembers.get(selectedRef)) || [];
  const selectedHazards = (selectedRef && refToHazards.get(selectedRef)) || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Paper elevation={1} sx={{ p: { xs: 1, sm: 2 }, zIndex: 10 }}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link component={RouterLink} to="/dashboard" color="inherit">Dashboard</Link>
          <Link component={RouterLink} to="/admin/tenants" color="inherit">Tenants</Link>
          <Link component={RouterLink} to={`/admin/tenants/edit/${tenantId}`} color="inherit">
            {tenant?.name || `Tenant ${tenantId}`}
          </Link>
          <Typography color="text.primary">Parcels Map</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
            Parcels Map: {tenant?.name}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {parcelCount != null && (
              <Chip size="small" label={`${parcelCount.toLocaleString()} parcels`} />
            )}
            <Chip size="small" color="success" variant="outlined" label={`${linkedRefs.size} linked`} />
            {!boundaries.length && <Chip size="small" color="warning" label="No boundary set" />}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              component={RouterLink}
              to={`/admin/tenants/edit/${tenantId}`}
            >
              Back
            </Button>
          </Stack>
        </Box>
        {pageError && <Alert severity="error" sx={{ mt: 1 }}>{pageError}</Alert>}
        {!boundaries.length && (
          <Alert severity="info" sx={{ mt: 1 }}>
            This tenant has no boundaries yet — no parcels to render. Create one in{' '}
            <Link component={RouterLink} to={`/fpa-settings?tab=boundaries&tenant_id=${tenantId}`}>
              Boundaries
            </Link>{' '}then re-run the parcel reindex.
          </Alert>
        )}
      </Paper>

      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <MapCanvas
          center={center}
          zoom={11}
          variant="screen"
          gearMode="full"
          tenantId={tenantId}
          forceParcels
          forceBoundaries
          mapSearch
          boundaryColorMode="indexed"
          parcelHazardByRef={refToHazards}
          parcelRefreshKey={refreshKey}
          onParcelSelect={handleParcelSelect}
          onParcelsLoaded={setParcelCount}
          className="!h-full !min-h-0 !rounded-none !border-0"
          legend={<MapLegend items={legendItems} />}
        />

        {/* FPA quick-view — compact summary + jump to full settings.
            `top: 172` clears MapCanvas's top-right icon stack (search +
            maximize + gear, each 40px with 8px gaps — ~148px tall from its
            own 12px offset) so this Paper's higher z-index doesn't hide
            those buttons underneath it. */}
        <Paper
          elevation={3}
          sx={{
            position: 'absolute', top: 172, right: 12, zIndex: 1000,
            p: 1.5, width: 260, maxHeight: 'calc(100% - 184px)', overflowY: 'auto',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <SettingsIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ flexGrow: 1, lineHeight: 1.2 }}>
              {tenant?.name || 'FPA'}
            </Typography>
          </Stack>
          {tenant?.area && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {tenant.area}
            </Typography>
          )}
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={0.5} sx={{ mb: 1.25 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <StarIcon fontSize="inherit" sx={{ color: '#ff9800' }} />
              <Typography variant="caption">
                <strong>Primary:</strong> {boundaries.find((b) => b.is_primary)?.name || '— none set —'}
              </Typography>
            </Stack>
            <Typography variant="caption"><strong>Boundaries:</strong> {boundaries.length}</Typography>
            <Typography variant="caption">
              <strong>Parcels:</strong> {parcelCount != null ? parcelCount.toLocaleString() : '…'}
            </Typography>
            <Typography variant="caption"><strong>Linked members:</strong> {linkedRefs.size}</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              <strong>Last reindex:</strong>{' '}
              {cacheStatus?.last_reindex_at
                ? new Date(cacheStatus.last_reindex_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                : 'not yet run'}
            </Typography>
          </Stack>
          <Button
            size="small"
            fullWidth
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            component={RouterLink}
            to={`/fpa-settings?tenant_id=${tenantId}`}
          >
            Open full settings
          </Button>
        </Paper>
      </Box>

      <ParcelDetailDrawer
        open={Boolean(selectedParcel)}
        onOpenChange={(o) => { if (!o) setSelectedParcel(null); }}
        parcel={selectedParcel}
        members={selectedMembers}
        hazards={selectedHazards}
        onManageMembers={handleManageMembers}
      />

      <MemberLinkDialog
        open={Boolean(linkTarget)}
        onClose={() => setLinkTarget(null)}
        parcelRef={linkTarget?.parcelRef}
        parcelLabel={linkTarget?.parcelLabel}
        initialSearch={linkTarget?.parcelLabel}
        tenantId={tenantId}
        onChange={handleLinkChanged}
      />
    </Box>
  );
};

export default TenantParcelsMap;
